import { useEffect, useMemo, useRef, useState } from "react";
import { academyRequest, createCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import { IDENTITY_DIRECTORY_CHANGED } from "./IdentityAssignmentFields";
import { TENANCY_DIRECTORY_CHANGED, type TenancyKind } from "./useTenancyDirectory";
import type { TenancyEntity, TenancyMembership, TenancyUnit, TenancyWorkspace } from "./generated/tenancy";

type RecordInput = Record<string, unknown>;
type Page<T> = { items: T[]; hasMore: boolean; nextCursor: string | null };
const unitTypes: Record<string, TenancyUnit["unitType"][]> = {
  "people-teams": ["team", "department"],
  campuses: ["campus"],
  "schools-faculties": ["school", "faculty"],
  departments: ["department"],
  "academic-units": ["academic_unit"],
};
const label = (value: string) => value.replace(/[-_]/g, " ");
const localDate = (value: unknown) => {
  if (!value) return "";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime())
    ? ""
    : new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
async function pages<T>(path: string, signal: AbortSignal) {
  const items: T[] = [],
    seen = new Set<string>();
  let cursor: string | null = null;
  do {
    const page: Page<T> = await academyRequest(
      `${path}?limit=100${cursor ? `&after=${encodeURIComponent(cursor)}` : ""}`,
      undefined,
      "GET",
      true,
      undefined,
      signal,
    );
    items.push(...page.items);
    cursor = page.hasMore ? page.nextCursor : null;
    if (page.hasMore && (!cursor || seen.has(cursor)))
      throw new Error("The directory did not advance. Reload the options.");
    if (cursor) seen.add(cursor);
  } while (cursor);
  return items;
}

/** Fields inside the existing portal action dialog, using the canonical D03 contracts. */
export function TenancyActionFields(props: {
  kind: TenancyKind;
  route: string;
  record?: RecordInput;
  formId: string;
  onSaved: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const account = useIdentity();
  const membership = account.access?.memberships.find(m => m.workspaceId === account.session?.workspace.id);
  const entityId = props.kind === "organization" ? membership?.organizationId : membership?.institutionId;
  const context = `${account.session?.tenantId}:${account.session?.user.id}:${account.session?.workspace.id}`;
  if (!entityId || !account.session)
    return <p role="alert">Load your current entity membership before making a change.</p>;
  return (
    <Fields
      key={`${context}:${entityId}:${props.route}:${props.record?.id || "create"}`}
      {...props}
      entityId={entityId}
      context={context}
      accountId={account.session.user.id}
      permissions={account.session.permissions}
    />
  );
}
function Fields({
  kind,
  route,
  record,
  formId,
  onSaved,
  onBusy,
  entityId,
  context,
  accountId,
  permissions,
}: Parameters<typeof TenancyActionFields>[0] & {
  entityId: string;
  context: string;
  accountId: string;
  permissions: string[];
}) {
  const memberMode = !!record?.personId;
  const base = `tenancy/${kind}/${entityId}`;
  const [profile, setProfile] = useState<TenancyEntity>();
  const [units, setUnits] = useState<TenancyUnit[]>([]),
    [workspaces, setWorkspaces] = useState<TenancyWorkspace[]>([]);
  const [members, setMembers] = useState<TenancyMembership[]>([]);
  const [action, setAction] = useState(memberMode ? "validity" : record ? "update" : "create");
  const [name, setName] = useState(String(record?.name || "")),
    [parent, setParent] = useState(String(record?.parentId || ""));
  const [unitType, setUnitType] = useState<TenancyUnit["unitType"]>(unitTypes[route]?.[0] || "team");
  const [personId, setPersonId] = useState("");
  const [scope, setScope] = useState(`${kind}:${entityId}`);
  const [target, setTarget] = useState<RecordInput | undefined>(record);
  const [from, setFrom] = useState(localDate(record?.validFrom)),
    [until, setUntil] = useState(localDate(record?.validUntil));
  const [reason, setReason] = useState(""),
    [error, setError] = useState("");
  const [loading, setLoading] = useState(true),
    [optionsLoading, setOptionsLoading] = useState(false),
    [busy, setBusy] = useState(false),
    [revision, setRevision] = useState(0);
  const locked = useRef(false),
    alive = useRef(true),
    send = useMemo(() => createCommandSender({ headerOnly: true }), []);
  const allowed = (permission: string) => permissions.includes(`tenancy.${kind}.${permission}`);
  async function checkContext() {
    const session = await checkIdentitySession();
    if (`${session.tenantId}:${session.user.id}:${session.workspace.id}` !== context)
      throw new Error("Account or workspace changed. Reopen the dialog.");
  }
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      onBusy(false);
    };
  }, [onBusy]);
  useEffect(() => {
    let current = true;
    const abort = new AbortController();
    setLoading(true);
    setError("");
    void (async () => {
      await checkContext();
      const [entity, structure, spaces] = await Promise.all([
        academyRequest<TenancyEntity>(base, undefined, "GET", true, undefined, abort.signal),
        pages<TenancyUnit>(base + "/units", abort.signal),
        memberMode ? pages<TenancyWorkspace>(base + "/workspaces", abort.signal) : Promise.resolve([]),
      ]);
      await checkContext();
      if (current) {
        setProfile(entity);
        setUnits(structure);
        setWorkspaces(spaces);
      }
    })()
      .catch(cause => {
        if (current) setError(cause instanceof Error ? cause.message : "Unable to load scope options.");
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
      abort.abort();
    };
  }, [base, revision]);
  useEffect(() => {
    const assignment = !memberMode && ["assign-lead", "assign-member"].includes(action);
    if (!assignment && !memberMode) return;
    if (memberMode && scope === `${kind}:${entityId}`) {
      setTarget(record);
      setFrom(localDate(record?.validFrom));
      setUntil(localDate(record?.validUntil));
      setOptionsLoading(false);
      return;
    }
    let current = true;
    const abort = new AbortController();
    setOptionsLoading(true);
    setError("");
    if (memberMode) setTarget(undefined);
    else setMembers([]);
    const [type, id] = scope.split(":");
    const path = assignment
      ? `${base}/members`
      : `${base}/${type === "unit" ? "units" : "workspaces"}/${id}/members`;
    void (async () => {
      await checkContext();
      const values = await pages<TenancyMembership>(path, abort.signal);
      await checkContext();
      if (!current) return;
      if (memberMode) {
        const selected = values.find(m => m.personId === record?.personId);
        setTarget(selected ? { ...selected } : undefined);
        setFrom(localDate(selected?.validFrom));
        setUntil(localDate(selected?.validUntil));
        setAction(selected ? "validity" : type === "unit" ? "assign-member" : "");
      } else setMembers(values);
    })()
      .catch(cause => {
        if (current) setError(cause instanceof Error ? cause.message : "Unable to load current members.");
      })
      .finally(() => {
        if (current) setOptionsLoading(false);
      });
    return () => {
      current = false;
      abort.abort();
    };
  }, [memberMode ? scope : action, base, revision]);
  const unit = units.find(u => u.id === String(record?.id));
  const choices = memberMode
    ? target
      ? target.status === "active"
        ? ["validity", "suspend", "revoke"]
        : target.status === "suspended"
          ? ["validity", "reactivate", "revoke"]
          : target.status === "pending"
            ? ["revoke"]
            : []
      : scope.startsWith("unit:") && record?.status === "active"
        ? ["assign-member"]
        : []
    : !record
      ? ["create"]
      : record.status === "active"
        ? ["update", "assign-lead", "assign-member", "deactivate"]
        : record.status === "inactive"
          ? ["update", "reactivate", "archive"]
          : [];
  const available = choices.filter(value =>
    allowed(memberMode || value.startsWith("assign-") ? "members.manage" : "structure.manage"),
  );
  const selectedAction = available.includes(action) ? action : available[0] || "";
  const parents = units.filter(candidate => {
    if (candidate.status !== "active" || candidate.id === String(record?.id)) return false;
    let parentId: string | null = candidate.parentId;
    const seen = new Set<string>();
    while (parentId && !seen.has(parentId)) {
      if (parentId === String(record?.id)) return false;
      seen.add(parentId);
      parentId = units.find(item => item.id === parentId)?.parentId || null;
    }
    return true;
  });
  async function submit() {
    if (
      locked.current ||
      loading ||
      optionsLoading ||
      !selectedAction ||
      !profile ||
      (unit && record && unit.version !== record.version)
    )
      return;
    locked.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      await checkContext();
      let path: string, body: Record<string, unknown>;
      if (selectedAction === "create") {
        path = `${base}/units/commands/create`;
        body = { name: name.trim(), unitType, ...(parent ? { parentId: parent } : {}), reason };
      } else if (memberMode) {
        const [type, scopeId] = scope.split(":");
        if (selectedAction === "assign-member") {
          const selectedUnit = units.find(u => u.id === scopeId);
          if (!selectedUnit) throw new Error("Choose an available unit.");
          path = `${base}/units/${selectedUnit.id}/commands/assign-member`;
          body = { version: selectedUnit.version, personId: record?.personId, reason };
        } else {
          if (!target) throw new Error("Select a current membership.");
          path = `${base}/members/${type}/${String(target.id)}/commands/${selectedAction}`;
          body = { version: target.version, reason };
          if (selectedAction === "validity") {
            const start = new Date(from),
              end = until ? new Date(until) : undefined;
            if (
              !Number.isFinite(start.getTime()) ||
              (end && (!Number.isFinite(end.getTime()) || end <= start))
            )
              throw new Error("Choose a valid start time and an end time after it.");
            body = {
              ...body,
              validFrom: start.toISOString(),
              ...(end ? { validUntil: end.toISOString() } : {}),
            };
          }
        }
      } else {
        path = `${base}/units/${String(record?.id)}/commands/${selectedAction}`;
        body = { version: record?.version, reason };
        if (selectedAction === "update")
          body = { ...body, name: name.trim(), ...(parent ? { parentId: parent } : {}) };
        if (selectedAction.startsWith("assign-")) body = { ...body, personId };
      }
      await send(path, body);
      await checkContext();
      if (alive.current) {
        window.dispatchEvent(new Event(TENANCY_DIRECTORY_CHANGED));
        window.dispatchEvent(new Event(IDENTITY_DIRECTORY_CHANGED));
        onSaved();
      }
    } catch (cause) {
      if (alive.current) setError(cause instanceof Error ? cause.message : "Unable to record change.");
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
      onBusy(false);
    }
  }
  return (
    <form
      id={formId}
      onSubmit={event => {
        event.preventDefault();
        void submit();
      }}
    >
      {error && <p role="alert">{error}</p>}
      {loading && <p role="status">Loading authorized scope options…</p>}
      {!loading && !profile && (
        <button type="button" onClick={() => setRevision(v => v + 1)}>
          Reload options
        </button>
      )}
      <fieldset
        disabled={busy || loading || optionsLoading || !profile}
        style={{ border: 0, margin: 0, padding: 0 }}
      >
        <div className="form-fields-v2">
          <label className="form-field-v2 full">
            Entity
            <input readOnly value={profile?.name || ""} />
          </label>
          {memberMode && (
            <label className="form-field-v2 full">
              Membership scope
              <select
                aria-label="Membership scope"
                value={scope}
                onChange={event => setScope(event.target.value)}
              >
                <option value={`${kind}:${entityId}`}>Current {kind}</option>
                {units.map(item => (
                  <option key={item.id} value={`unit:${item.id}`}>
                    {item.name} · {label(item.unitType)}
                  </option>
                ))}
                {workspaces.map(item => (
                  <option key={item.id} value={`workspace:${item.id}`}>
                    {item.name} · workspace
                  </option>
                ))}
              </select>
            </label>
          )}
          {record && (
            <label className="form-field-v2 full">
              Action
              <select
                aria-label="Action"
                required
                value={selectedAction}
                onChange={event => {
                  setAction(event.target.value);
                  setPersonId("");
                }}
              >
                {!available.length && <option value="">No permitted change in this scope</option>}
                {available.map(value => (
                  <option key={value} value={value}>
                    {label(value)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {["create", "update"].includes(selectedAction) && (
            <>
              <label className="form-field-v2 full">
                Name / title
                <input
                  required
                  maxLength={200}
                  value={name}
                  onChange={event => setName(event.target.value)}
                />
              </label>
              {!record && (
                <label className="form-field-v2">
                  Unit type
                  <select
                    aria-label="Unit type"
                    value={unitType}
                    onChange={event => setUnitType(event.target.value as TenancyUnit["unitType"])}
                  >
                    {(unitTypes[route] || []).map(value => (
                      <option key={value} value={value}>
                        {label(value)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="form-field-v2">
                Parent unit
                <select
                  aria-label="Parent unit"
                  value={parent}
                  onChange={event => setParent(event.target.value)}
                >
                  <option value="">Entity root</option>
                  {parents.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} · {label(item.unitType)}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          {!memberMode && selectedAction.startsWith("assign-") && (
            <label className="form-field-v2 full">
              Person
              <select
                aria-label="Person"
                required
                value={personId}
                onChange={event => setPersonId(event.target.value)}
              >
                <option value="">Choose a current entity member</option>
                {members
                  .filter(
                    m =>
                      m.status === "active" &&
                      m.accountId !== accountId &&
                      new Date(m.validFrom) <= new Date() &&
                      (!m.validUntil || new Date(m.validUntil) > new Date()),
                  )
                  .map(m => (
                    <option key={m.id} value={m.personId}>
                      {m.name || m.personId}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {memberMode && selectedAction === "validity" && (
            <>
              <label className="form-field-v2">
                Valid from
                <input
                  type="datetime-local"
                  required
                  value={from}
                  onChange={event => setFrom(event.target.value)}
                />
              </label>
              <label className="form-field-v2">
                Valid until (optional)
                <input type="datetime-local" value={until} onChange={event => setUntil(event.target.value)} />
              </label>
              <p>
                Dates use your device’s local time zone. An empty end date means no expiry, if your authority
                permits it.
              </p>
            </>
          )}
          <label className="form-field-v2 full">
            Reason
            <textarea
              aria-label="Reason"
              required
              minLength={3}
              maxLength={1000}
              value={reason}
              onChange={event => setReason(event.target.value)}
            />
          </label>
        </div>
        {!selectedAction && <p>No change is permitted for this record in the selected scope.</p>}
        {unit && record && unit.version !== record.version && (
          <p role="alert">This record changed. Close and reopen it to review the current version.</p>
        )}
      </fieldset>
    </form>
  );
}
